import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { eur } from "@/lib/api";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import AnimatedNumber from "@/components/AnimatedNumber";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import NotaryFeesBlock from "@/components/NotaryFeesBlock";
import { toast } from "sonner";
import { Calculator, ArrowRight, ArrowLeft, CheckCircle2 } from "lucide-react";

const STEPS = ["Bien", "Localisation", "État & DPE", "Vos coordonnées"];

export default function Estimer() {
  const [step, setStep] = useState(0);
  const [f, setF] = useState({
    property_type: "appartement", surface: 80, rooms: 3,
    city: "", postal_code: "",
    condition: "bon", dpe: "D", year_built: 1980,
    is_occupied: "non", owner_age: 70,
    name: "", email: "", phone: "",
  });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setF({ ...f, [k]: v });

  const next = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
  const prev = () => setStep((s) => Math.max(0, s - 1));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/estimate", {
        property_type: f.property_type, surface: Number(f.surface), rooms: Number(f.rooms),
        city: f.city, postal_code: f.postal_code, condition: f.condition, dpe: f.dpe,
        year_built: Number(f.year_built),
        is_occupied: f.is_occupied === "oui", owner_age: Number(f.owner_age),
        name: f.name, email: f.email, phone: f.phone,
      });
      setResult(data);
      toast.success("Estimation prête — un conseiller vous recontacte sous 24 h.");
    } catch { toast.error("Erreur d'envoi, réessayez."); }
    finally { setBusy(false); }
  };

  const simulatorLink = useMemo(() => {
    if (!result) return "/simulateur";
    const params = new URLSearchParams({
      market_value: String(result.estimated_value),
      property_type: f.is_occupied === "oui" ? "occupee" : "libre",
    });
    return `/simulateur?${params.toString()}`;
  }, [result, f.is_occupied]);

  return (
    <>
      <SEO
        title="Estimer votre bien — Vente à terme — venteaterme.fr"
        description="Estimation gratuite et sans engagement de la valeur de votre bien en vente à terme. Résultat immédiat, transmission d'un rapport détaillé par email."
      />

      <section className="pt-24 pb-12 bg-navy text-cream">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3 mb-4">
            <Calculator className="text-accent-brand" size={22} />
            <div className="text-xs uppercase tracking-[0.3em] text-accent-brand font-bold">Estimation</div>
          </div>
          <h1 className="font-serif text-4xl md:text-6xl leading-tight">Estimez votre bien en 2 minutes.</h1>
          <p className="text-cream/80 mt-6 max-w-2xl">Un résultat immédiat basé sur les données du marché local. Puis un rapport détaillé envoyé par un conseiller sous 24 h.</p>
          <p className="text-cream mt-4 max-w-2xl font-semibold">
            <span className="inline-block bg-accent-brand text-white text-xs uppercase tracking-widest font-bold px-3 py-1.5 mr-3">100&nbsp;% gratuit</span>
            Sans engagement, sans commission.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-3xl mx-auto px-6">
          {result ? (
            <Reveal className="bg-white border border-navy/10 p-10">
              <div className="text-xs uppercase tracking-wider text-accent-brand font-bold mb-3">Estimation indicative</div>
              <h2 className="font-serif text-3xl text-navy mb-2 text-balance">Votre bien vaudrait environ&nbsp;:</h2>
              <div className="font-serif text-5xl md:text-6xl text-accent-brand font-semibold mb-4 tabular-nums">
                <AnimatedNumber value={result.estimated_value} format={(n) => eur(Math.round(n))} />
              </div>
              <div className="text-sm text-navy/70 mb-8 tabular-nums">Fourchette&nbsp;: <strong>{eur(result.low)}</strong> — <strong>{eur(result.high)}</strong> (basé sur {eur(result.price_per_sqm)}/m² sur {f.city || "votre secteur"}).</div>

              <ul className="text-sm text-navy/75 space-y-2 mb-8 border-t border-navy/10 pt-6">
                {result.factors?.map((x, i) => (
                  <li key={i} className="flex items-start gap-2"><CheckCircle2 size={16} className="text-accent-brand mt-0.5 shrink-0" /> {x}</li>
                ))}
              </ul>

              <div className="flex flex-col sm:flex-row gap-3">
                <Link to={simulatorLink} data-testid="est-cta-simulator" className="inline-flex items-center justify-center gap-2 bg-accent-brand text-white px-6 py-3 font-bold tracking-wide hover:bg-[#B08D22] transition-colors">
                  Lancer le simulateur avec cette valeur <ArrowRight size={18} />
                </Link>
                <Link to="/deposer-annonce" className="inline-flex items-center justify-center gap-2 border-2 border-navy text-navy px-6 py-3 font-bold tracking-wide hover:bg-navy hover:text-cream transition-colors">
                  Déposer une annonce
                </Link>
              </div>
              <div className="mt-8">
                <NotaryFeesBlock price={result.estimated_value} defaultKind={Number(f.year_built) >= 2020 ? "neuf" : "ancien"} />
              </div>

              <button
                type="button"
                data-testid="est-pdf-btn"
                onClick={async () => {
                  const propertyType = f.is_occupied === "oui" ? "occupee" : "libre";
                  const retained = result.estimated_value;
                  const defaultBouquet = Math.round(retained * 0.20);
                  const duration = 15;
                  const monthly = Math.round((retained - defaultBouquet) / (duration * 12));
                  const { generateRecapPDF } = await import("@/lib/generatePdf");
                  generateRecapPDF({
                    context: "estimation",
                    propertyType,
                    marketValue: retained,
                    retainedValue: retained,
                    bouquet: defaultBouquet,
                    monthlyPayment: monthly,
                    duration,
                    notaryKind: Number(f.year_built) >= 2020 ? "neuf" : "ancien",
                    city: f.city || "",
                    estimatedValue: retained,
                  });
                }}
                className="mt-6 w-full border-2 border-navy text-navy py-3 font-bold tracking-wide hover:bg-navy hover:text-cream transition-colors"
              >
                Télécharger le récapitulatif (PDF)
              </button>

              <p className="text-xs text-navy/50 mt-6">Estimation indicative fondée sur des données de marché. Ne remplace pas une expertise chez notaire ou expert immobilier.</p>
            </Reveal>
          ) : (
            <form onSubmit={submit} data-testid="estimate-form" className="bg-white border border-navy/10 p-8 md:p-10">
              {/* Steps indicator */}
              <div className="flex items-center gap-2 mb-8">
                {STEPS.map((s, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <div className={`w-7 h-7 flex items-center justify-center font-bold ${i === step ? "bg-accent-brand text-white" : i < step ? "bg-navy text-cream" : "bg-navy/10 text-navy/50"}`}>{i + 1}</div>
                    {i < STEPS.length - 1 && <div className={`w-8 h-px ${i < step ? "bg-navy" : "bg-navy/15"}`} />}
                  </div>
                ))}
              </div>
              <div className="text-xs uppercase tracking-widest text-accent-brand font-bold mb-2">Étape {step + 1} / {STEPS.length}</div>
              <h2 className="font-serif text-2xl text-navy mb-6">{STEPS[step]}</h2>

              {step === 0 && (
                <div className="space-y-5">
                  <div>
                    <Label>Type de bien</Label>
                    <RadioGroup value={f.property_type} onValueChange={(v) => set("property_type", v)} className="grid grid-cols-2 gap-3 mt-3">
                      {[["appartement","Appartement"],["maison","Maison"]].map(([v, lb]) => (
                        <label key={v} className={`cursor-pointer border p-3 flex items-center gap-2 text-sm ${f.property_type === v ? "border-accent-brand bg-accent-brand/5" : "border-navy/15"}`}>
                          <RadioGroupItem value={v} id={`pt-${v}`} /><span>{lb}</span>
                        </label>
                      ))}
                    </RadioGroup>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Surface (m²)</Label><Input data-testid="est-surface" type="number" required min={10} value={f.surface} onChange={(e) => set("surface", e.target.value)} className="mt-2 border-navy/20" /></div>
                    <div><Label>Nombre de pièces</Label><Input type="number" required min={1} max={20} value={f.rooms} onChange={(e) => set("rooms", e.target.value)} className="mt-2 border-navy/20" /></div>
                  </div>
                </div>
              )}

              {step === 1 && (
                <div className="space-y-5">
                  <div><Label>Ville</Label><Input required data-testid="est-city" value={f.city} onChange={(e) => set("city", e.target.value)} placeholder="Paris, Lyon, Marseille…" className="mt-2 border-navy/20" /></div>
                  <div><Label>Code postal</Label><Input value={f.postal_code} onChange={(e) => set("postal_code", e.target.value)} className="mt-2 border-navy/20" /></div>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-5">
                  <div>
                    <Label>État général</Label>
                    <Select value={f.condition} onValueChange={(v) => set("condition", v)}>
                      <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="neuf">Neuf / rénové récemment</SelectItem>
                        <SelectItem value="bon">Bon état</SelectItem>
                        <SelectItem value="rafraichir">À rafraîchir</SelectItem>
                        <SelectItem value="renover">À rénover</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Classe DPE</Label>
                      <Select value={f.dpe} onValueChange={(v) => set("dpe", v)}>
                        <SelectTrigger data-testid="est-dpe" className="mt-2"><SelectValue /></SelectTrigger>
                        <SelectContent>{["A","B","C","D","E","F","G"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Année de construction</Label><Input type="number" value={f.year_built} onChange={(e) => set("year_built", e.target.value)} className="mt-2 border-navy/20" /></div>
                  </div>
                  <div>
                    <Label>Bien occupé par le vendeur ?</Label>
                    <RadioGroup value={f.is_occupied} onValueChange={(v) => set("is_occupied", v)} className="grid grid-cols-2 gap-3 mt-3">
                      {[["non","Libre à la vente"],["oui","Vendeur occupant (DUH)"]].map(([v, lb]) => (
                        <label key={v} className={`cursor-pointer border p-3 flex items-center gap-2 text-sm ${f.is_occupied === v ? "border-accent-brand bg-accent-brand/5" : "border-navy/15"}`}>
                          <RadioGroupItem value={v} id={`oc-${v}`} /><span>{lb}</span>
                        </label>
                      ))}
                    </RadioGroup>
                  </div>
                  {f.is_occupied === "oui" && (
                    <div><Label>Âge du vendeur (occupant)</Label><Input type="number" value={f.owner_age} onChange={(e) => set("owner_age", e.target.value)} className="mt-2 border-navy/20" /></div>
                  )}
                </div>
              )}

              {step === 3 && (
                <div className="space-y-4">
                  <p className="text-sm text-navy/70">Renseignez vos coordonnées pour recevoir l&rsquo;estimation détaillée par email.</p>
                  <div><Label>Nom</Label><Input required data-testid="est-name" value={f.name} onChange={(e) => set("name", e.target.value)} className="mt-2 border-navy/20" /></div>
                  <div><Label>Email</Label><Input required type="email" data-testid="est-email" value={f.email} onChange={(e) => set("email", e.target.value)} className="mt-2 border-navy/20" /></div>
                  <div><Label>Téléphone</Label><Input required data-testid="est-phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} className="mt-2 border-navy/20" /></div>
                  <p className="text-xs text-navy/50">Vos coordonnées ne sont utilisées que pour vous transmettre l&rsquo;estimation. Voir notre <Link to="/confidentialite" className="underline">politique de confidentialité</Link>.</p>
                </div>
              )}

              <div className="flex justify-between mt-8 pt-6 border-t border-navy/10">
                {step > 0 ? (
                  <button type="button" onClick={prev} className="inline-flex items-center gap-2 border border-navy/20 px-5 py-3 text-sm font-bold text-navy hover:bg-navy hover:text-cream transition-colors">
                    <ArrowLeft size={16} /> Précédent
                  </button>
                ) : <span />}
                {step < STEPS.length - 1 ? (
                  <button type="button" data-testid="est-next" onClick={next} className="inline-flex items-center gap-2 bg-navy text-cream px-5 py-3 text-sm font-bold hover:bg-navy/90 transition-colors ml-auto">
                    Suivant <ArrowRight size={16} />
                  </button>
                ) : (
                  <button type="submit" disabled={busy} data-testid="est-submit" className="inline-flex items-center gap-2 bg-accent-brand text-white px-6 py-3 text-sm font-bold hover:bg-[#B08D22] transition-colors ml-auto disabled:opacity-60">
                    {busy ? "Calcul..." : "Obtenir mon estimation"} <ArrowRight size={16} />
                  </button>
                )}
              </div>
            </form>
          )}
        </div>
      </section>
    </>
  );
}
